import { graphql } from "gatsby"
import rangeParser from "parse-numeric-range"
import Highlight, { defaultProps, Language } from "prism-react-renderer"
import Prism from "prism-react-renderer/prism"
import theme from "prism-react-renderer/themes/vsDark"
import React from "react"
import useCopyToClipboard from "../../util/useCopyToClipboard"
import {
  CodeBlockWrapper,
  CopyCode,
  LineNo,
  Pre,
  PreHeader,
} from "./code-block.style"
;(typeof global !== "undefined" ? global : window).Prism = Prism
// Add extra prismjs languages here
// included prism-react-renderer languages (v1.2.1): https://github.com/FormidableLabs/prism-react-renderer/blob/844cec385460cfe032309880b8ca2bfb5f203ab0/src/vendor/prism/includeLangs.js
require("prismjs/components/prism-kotlin")
require("prismjs/components/prism-swift")
require("prismjs/components/prism-objectivec")
require("prismjs/components/prism-java")
require("prismjs/components/prism-dart")

export const CodeBlockFragment = graphql`
  fragment CodeBlockFragment on ContentfulCodeBlock {
    contentful_id
    __typename
    code {
      code
    }
    language
    settings {
      key
      value
    }
  }
`

interface ICodeBlockProps {
  className?: string
  children: string
  language: Language
  title?: string
  highlight?: string
  lineNumbers?: boolean
}

// ref: https://prince.dev/highlight-with-react
const calculateLinesToHighlight = (meta) => {
  const RE = /{([\d,-]+)}/
  if (RE.test(meta)) {
    const strlineNumbers = RE.exec(meta)?.[1] || ""
    const lineNumbers = rangeParser(strlineNumbers)
    return (index) => lineNumbers.includes(index + 1)
  } else {
    return () => false
  }
}

const CodeBlock = ({
  children,
  language,
  title,
  highlight,
  lineNumbers,
}: ICodeBlockProps) => {
  const code = children.trim()
  const shouldHighlightLine = calculateLinesToHighlight(highlight)
  const [isCopied, copyToClipboard] = useCopyToClipboard(code, 5000)

  return (
    <div style={{ margin: "24px 0 24px 0" }}>
      {title && <PreHeader>{title}</PreHeader>}
      <CodeBlockWrapper>
        <Highlight
          {...defaultProps}
          code={code}
          language={language}
          theme={theme}
        >
          {({ className, style, tokens, getLineProps, getTokenProps }) => (
            <Pre
              className={className}
              style={{ ...style, backgroundColor: `var(--tricorn-black)` }}
              hasTitle={!!title}
            >
              <CopyCode
                onClick={copyToClipboard}
                disabled={isCopied}
                hasTitle={!!title}
              >
                {isCopied ? "Copied!" : "Copy"}
              </CopyCode>
              <code>
                {tokens.map((line, index) => {
                  const lineProps = getLineProps({ line, key: index })

                  if (shouldHighlightLine(index)) {
                    lineProps.className = `${lineProps.className} highlight-line`
                  }

                  return (
                    <div key={index} {...lineProps}>
                      {lineNumbers && <LineNo>{index + 1}</LineNo>}
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token, key })} />
                      ))}
                    </div>
                  )
                })}
              </code>
            </Pre>
          )}
        </Highlight>
      </CodeBlockWrapper>
    </div>
  )
}

export default CodeBlock
